import { validRange } from '../../../utils/dateRanges';
import DateRangeControl from '../../common/DateRangeControl';
import React, { useState, useEffect } from "react";
import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button, Stack, Paper } from "@mui/material";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { Info as InfoIcon } from "@mui/icons-material";
import { format, subDays, differenceInDays } from "date-fns";
import { apiService } from "../../../config/api";
import { API_ENDPOINTS } from "../../../config/api";

interface AdherenceData {
  date: string;
  adherence: number;
  totalParticipants: number;
  validDayCount: number;
}

interface AdherenceSummary {
  totalDays: number;
  daysWithData: number;
  averageAdherence: number;
}

/**
 * Get color based on adherence percentage
 * > 90: Excellent (Success Green)
 * 80-90: Good (Secondary Teal)
 * 70-80: Fair (Purple)
 * 40-70: Poor (Warning Orange)
 * < 40: Critical (Error Red)
 */
const getAdherenceColor = (adherence: number): string => {
  if (adherence > 90) return "#4CAF50"; // Excellent - Success Green
  if (adherence >= 80) return "#00505C"; // Good - Secondary Teal
  if (adherence >= 70) return "#7C3AED"; // Fair - Purple
  if (adherence >= 40) return "#FF9800"; // Poor - Warning Orange
  return "#F44336"; // Critical - Error Red
};

const AvgStudyAdherence: React.FC = () => {
  const [data, setData] = useState<AdherenceData[]>([]);
  const [summary, setSummary] = useState<AdherenceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  useEffect(() => {
    fetchAdherenceData(startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchAdherenceData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await apiService.get(
        `${API_ENDPOINTS.ADHERENCE_MONITOR.DATA}?startDate=${start}&endDate=${end}`
      );

      if (response.success) {
        setData(response.data || []);
        setSummary(response.summary || null);
      }
    } catch (err: any) {
      // Handle error silently or log to console
      console.error("Error fetching adherence data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchAdherenceData(startDate, endDate);
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Section Title */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Average Study Adherence Overview
        </Typography>

        {/* Date Range Controls - Always Visible */}
        <Box sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
          >
            <DateRangeControl value={{ startDate: startDate || '', endDate: endDate || '' }}
              onChange={range => { setStartDate(range.startDate); setEndDate(range.endDate); }}
              startLabel="Start Date" endLabel="End Date" disabled={loading} />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateRange}
             disabled={loading || !validRange({ startDate, endDate })}>
              Apply
            </Button>
          </Stack>

          {/* Date Range Validation Error */}
          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {dateRangeError}
            </Alert>
          )}
        </Box>

        {/* Color Legend */}
        <Box
          sx={{
            display: "flex",
            gap: 3,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#4CAF50",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Excellent (&gt;90%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#00505C",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Good (80-90%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#7C3AED",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Fair (70-80%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#FF9800",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Poor (40-70%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#F44336",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Critical (&lt;40%)</Typography>
          </Box>
        </Box>

        {/* Chart */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : data && data.length > 0 ? (
          <Box>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                  interval={data.length > 30 ? Math.floor(data.length / 10) : 0}
                />
                <YAxis
                  label={{
                    value: "Adherence (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const data = payload[0].payload as AdherenceData;
                      return (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                          elevation={3}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ fontWeight: 600 }}
                          >
                            {data.date}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Adherence:{" "}
                            <strong>{data.adherence.toFixed(1)}%</strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {data.validDayCount} of {data.totalParticipants}{" "}
                            participants
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="adherence" radius={[6, 6, 0, 0]} minPointSize={2}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getAdherenceColor(entry.adherence)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Summary Stats */}
            {summary && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                  mt: 4,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Days in Range
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.totalDays}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Days with Data
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.daysWithData}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Average Adherence
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.averageAdherence.toFixed(1)}%
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No data available for the selected date range. The chart shows all
            dates in the range, but participant data has not yet been recorded
            for this period.
          </Alert>
        )}
        {/* How Adherence is Calculated - moved to bottom */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "info.lighter",
            border: "1px solid",
            borderColor: "info.light",
            borderRadius: 1,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
          }}
        >
          <InfoIcon
            sx={{
              fontSize: 20,
              color: "info.main",
              mt: 0.3,
              flexShrink: 0,
            }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              How Adherence is Calculated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Each day's adherence percentage is calculated as follows: For each
              study, we count participants with at least 10 hours of valid heart
              rate data (valid_day_10hr = true). The percentage shown is the
              average across all studies for that day: (total valid
              participants) / (total participants) × 100.
            </Typography>
          </Box>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default AvgStudyAdherence;
